import React, { useRef, useState, useEffect, useMemo } from 'react'
import BacktestReportCard from './BacktestReportCard'
import SessionAlbumCard from './SessionAlbumCard'
import Box from '@mui/material/Box'
import { Skeleton, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { VirtuosoGrid, VirtuosoGridHandle } from 'react-virtuoso'
import { Variant } from '@mui/material/styles/createTypography'
import VirtuosoListComponent from './VirtuosoListComponent'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'

type ReportCardListProps = {
  backtests?: any[]
  condensed?: boolean
  loadMore?: () => void
  isLoading?: boolean
  totalCount?: number
  emptyListText?: {
    title: string
    subTitle?: string
    size?: 'small' | 'medium' | 'large'
  }
  selectable?: boolean
  onSelected?: (selected: any) => void
  selectedBacktestId?: any
  headerComponent?: React.ReactNode
  activeBacktest?: any
  onBacktestSelection?: (backtest: any) => void
  hiddenBacktestIds?: Set<string>
  footerActions?: any[]
  onViewClick?: (backtest: any) => void
  disableGrouping?: boolean
}

function ReportCardList({
  backtests = [],
  condensed,
  loadMore,
  isLoading,
  totalCount = backtests?.length || 0,
  emptyListText,
  selectable,
  onSelected,
  headerComponent,
  activeBacktest,
  onBacktestSelection,
  hiddenBacktestIds = new Set(),
  footerActions,
  onViewClick,
  disableGrouping,
}: ReportCardListProps) {
  const { usermeta } = useApplicationSuiteContext()
  const virtuosoRef = useRef<VirtuosoGridHandle>(null)
  const [prevBacktestsLength, setPrevBacktestsLength] = useState(backtests.length)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [visibleRange, setVisibleRange] = useState<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: 0,
  })
  // Track backtests that are currently fading out (to keep them in DOM during animation)
  const [fadingOutIds, setFadingOutIds] = useState<Set<string>>(new Set())
  const fadingOutIdsRef = useRef<Set<string>>(new Set())
  const previousHiddenIdsRef = useRef<Set<string>>(new Set())

  // Sync ref with state
  useEffect(() => {
    fadingOutIdsRef.current = fadingOutIds
  }, [fadingOutIds])

  // Track which backtests just became hidden (to trigger fade-out animation)
  useEffect(() => {
    if (!hiddenBacktestIds) {
      previousHiddenIdsRef.current = new Set()
      return
    }

    const previousHidden = previousHiddenIdsRef.current
    const newlyHiddenIds: string[] = []

    hiddenBacktestIds.forEach((id) => {
      if (!previousHidden.has(id)) {
        newlyHiddenIds.push(id)
      }
    })

    // Update the ref so subsequent renders know which ids have already been processed
    previousHiddenIdsRef.current = new Set(hiddenBacktestIds)

    if (newlyHiddenIds.length === 0) {
      return
    }

    const newlyHiddenSet = new Set(newlyHiddenIds)

    backtests.forEach((backtest) => {
      if (
        backtest?.id &&
        newlyHiddenSet.has(backtest.id) &&
        !fadingOutIdsRef.current.has(backtest.id)
      ) {
        // Mark as fading out
        fadingOutIdsRef.current.add(backtest.id)
        setFadingOutIds((prev) => new Set(prev).add(backtest.id))

        // After fade animation completes (500ms), remove from fading set
        window.setTimeout(() => {
          fadingOutIdsRef.current.delete(backtest.id)
          backtest.hidden = true
          setFadingOutIds((prev) => {
            const next = new Set(prev)
            next.delete(backtest.id)
            return next
          })
        }, 500)
      }
    })
  }, [hiddenBacktestIds, backtests])

  // Filter out hidden backtests (but keep ones that are still fading)
  const visibleBacktests = useMemo(() => {
    return backtests.filter((backtest) => {
      if (!backtest?.id) return false
      // Keep if it's fading out (show animation) or not hidden
      return fadingOutIds.has(backtest.id) || !backtest.hidden
    })
  }, [backtests, fadingOutIds])

  // Group AI-generated backtests sharing an agentSessionId into album items.
  // Preserves sort order: the album appears at the position of its first encountered member.
  // Sessions with only one loaded member render as solo cards.
  const groupedVisibleItems = useMemo(() => {
    if (disableGrouping) return visibleBacktests

    type SessionEntry = { cover: any; memberIds: string[]; latestTime: number }
    const sessionMap = new Map<string, SessionEntry>()
    // Placeholder marker so we can do a single-pass and replace at the end
    const result: Array<any | { _sessionId: string }> = []

    for (const backtest of visibleBacktests) {
      const sessionId: string | undefined = backtest.agentSessionId
      if (backtest.source === 'strategy-agent' && sessionId) {
        if (!sessionMap.has(sessionId)) {
          sessionMap.set(sessionId, {
            cover: backtest,
            memberIds: [backtest.id],
            latestTime: backtest.startTime || 0,
          })
          result.push({ _sessionId: sessionId })
        } else {
          const entry = sessionMap.get(sessionId)!
          entry.memberIds.push(backtest.id)
          if ((backtest.startTime || 0) > entry.latestTime) {
            entry.cover = backtest
            entry.latestTime = backtest.startTime || 0
          }
        }
      } else {
        result.push(backtest)
      }
    }

    return result.map((item) => {
      if ('_sessionId' in item) {
        const entry = sessionMap.get(item._sessionId)!
        if (entry.memberIds.length === 1) {
          return entry.cover
        }
        return {
          cover: entry.cover,
          sessionInfo: {
            sessionId: item._sessionId,
            count: entry.memberIds.length,
            backtestIds: entry.memberIds,
          },
        }
      }
      return item
    })
  }, [visibleBacktests, disableGrouping])

  // If the focused backtest becomes hidden, move focus to the next available backtest
  useEffect(() => {
    if (!activeBacktest?.id || !hiddenBacktestIds?.has(activeBacktest.id) || !onBacktestSelection) {
      return
    }

    const currentIndex = backtests.findIndex((backtest) => backtest?.id === activeBacktest.id)

    const findNextVisible = () => {
      if (currentIndex !== -1) {
        const nextCandidate = backtests
          .slice(currentIndex + 1)
          .find((backtest) => backtest?.id && !hiddenBacktestIds.has(backtest.id))
        if (nextCandidate) return nextCandidate

        for (let i = currentIndex - 1; i >= 0; i--) {
          const candidate = backtests[i]
          if (candidate?.id && !hiddenBacktestIds.has(candidate.id)) {
            return candidate
          }
        }
      }

      const firstVisible = backtests.find(
        (backtest) => backtest?.id && !hiddenBacktestIds.has(backtest.id)
      )
      if (firstVisible && firstVisible.id !== activeBacktest.id) {
        return firstVisible
      }

      return null
    }

    const nextBacktest = findNextVisible()
    onBacktestSelection(nextBacktest || null)
  }, [activeBacktest?.id, hiddenBacktestIds, backtests, onBacktestSelection])

  const activeIndex = useMemo(() => {
    if (!activeBacktest?.id) return -1
    return groupedVisibleItems.findIndex((item) =>
      item.sessionInfo ? item.cover?.id === activeBacktest.id : item.id === activeBacktest.id
    )
  }, [activeBacktest?.id, groupedVisibleItems])

  const isActiveVisible = useMemo(
    () =>
      activeIndex !== -1 &&
      activeIndex >= visibleRange.startIndex &&
      activeIndex <= visibleRange.endIndex,
    [activeIndex, visibleRange]
  )

  // Track when new data is loaded
  useEffect(() => {
    if (groupedVisibleItems.length > prevBacktestsLength) {
      setPrevBacktestsLength(groupedVisibleItems.length)
      setIsLoadingMore(false)

      if (virtuosoRef.current && groupedVisibleItems.length > prevBacktestsLength) {
        virtuosoRef.current.scrollToIndex({
          index: prevBacktestsLength - 1,
          align: 'end',
          behavior: 'auto',
        })
      }
    }
  }, [groupedVisibleItems.length, prevBacktestsLength])

  // Scroll to active backtest when it's in the list
  useEffect(() => {
    if (activeBacktest?.id && virtuosoRef.current && activeIndex !== -1) {
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({
          index: activeIndex,
          align: 'center',
          behavior: 'smooth',
        })
      }, 100)
    }
  }, [activeBacktest?.id, activeIndex])

  function renderNoBacktests() {
    let titleVariant: Variant = 'h5'
    let subTitleVariant: Variant = 'body2'

    if (emptyListText?.size === 'small') {
      titleVariant = 'h6'
      subTitleVariant = 'body2'
    } else if (emptyListText?.size === 'large') {
      titleVariant = 'h4'
      subTitleVariant = 'h6'
    }

    return (
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
        width={'100%'}
      >
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'} p={1}>
          <Typography variant={titleVariant} color={'textSecondary'} fontWeight={'normal'}>
            {emptyListText?.title}
          </Typography>
          {emptyListText?.subTitle && (
            <Typography variant={subTitleVariant} color={'textSecondary'} textAlign={'center'}>
              {emptyListText.subTitle}
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  if ((!groupedVisibleItems || groupedVisibleItems.length === 0) && !isLoading) {
    return (
      <Box
        p={1}
        sx={{
          backgroundColor: 'transparent',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
        }}
      >
        {renderNoBacktests()}
      </Box>
    )
  }

  if ((!groupedVisibleItems || groupedVisibleItems.length === 0) && isLoading) {
    return (
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
        width={'100%'}
      >
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <Typography variant={'h5'} color={'textSecondary'} fontWeight={'normal'}>
            Loading...
          </Typography>
        </Box>
      </Box>
    )
  }

  const handleEndReached = () => {
    if (loadMore && !isLoading && !isLoadingMore && visibleBacktests.length < totalCount) {
      setIsLoadingMore(true)
      loadMore()
    }
  }

  return (
    <Box position={'relative'} height={'100%'} width={'100%'}>
      <VirtuosoGrid
        ref={virtuosoRef}
        totalCount={groupedVisibleItems.length}
        style={{ height: '100%', width: '100%' }}
        computeItemKey={(index) => {
          const item = groupedVisibleItems[index]
          if (item?.sessionInfo) return item.sessionInfo.sessionId
          return item?.id || index
        }}
        overscan={800}
        endReached={handleEndReached}
        rangeChanged={({ startIndex, endIndex }) => setVisibleRange({ startIndex, endIndex })}
        components={{
          Header: () => <>{headerComponent}</>,
          List: VirtuosoListComponent,
          ScrollSeekPlaceholder: ({ height, width, index }) => (
            <div style={{ height, width: '100%', maxWidth: `350px` }}>
              <Skeleton variant="rounded" animation="wave" height="100%" />
            </div>
          ),
          Footer: () =>
            isLoading && groupedVisibleItems.length > 0 ? (
              <Box py={2} display="flex" justifyContent="center" width="100%">
                <Skeleton variant="rounded" width="80%" height={60} animation="wave" />
              </Box>
            ) : null,
        }}
        itemContent={(index) => {
          const item = groupedVisibleItems[index]
          if (item) {
            const isAlbum = !!item.sessionInfo
            const coverId = isAlbum ? item.cover?.id : item.id
            const isHiding = fadingOutIds.has(coverId)

            if (isAlbum) {
              return (
                <Box
                  sx={{
                    opacity: isHiding ? 0 : 1,
                    transition: 'opacity .5s ease-out',
                    pointerEvents: isHiding ? 'none' : 'auto',
                  }}
                >
                  <SessionAlbumCard
                    cover={item.cover}
                    sessionInfo={item.sessionInfo}
                    condensed={condensed}
                    isActive={activeBacktest?.id === item.cover?.id}
                    onBacktestSelection={onBacktestSelection}
                  />
                </Box>
              )
            }

            const backtest = item
            const footerActions: any[] = []
            if (selectable) {
              footerActions.push({
                type: 'SELECT',
                onSelect: (backtest: any) => onSelected && onSelected(backtest),
              })
            } else if (onViewClick) {
              footerActions.push({
                type: 'VIEW',
                onView: onViewClick,
              })
            } else {
              footerActions.push({
                type: 'OPEN_SETTINGS',
                onSettingsClick: (isActive: boolean) =>
                  onBacktestSelection && onBacktestSelection(isActive ? backtest : null),
              })
              if (backtest.isFeatured) {
                footerActions.push({ type: 'CREATE_ALERT' })
              }
              if (usermeta?.isAdmin) {
                footerActions.push({ type: 'RUN_AGENT_SESSION' })
              }
            }

            return (
              <Box
                sx={{
                  opacity: isHiding ? 0 : 1,
                  transition: 'opacity .5s ease-out',
                  pointerEvents: isHiding ? 'none' : 'auto',
                }}
              >
                <BacktestReportCard
                  condensed={condensed}
                  backtest={backtest}
                  isActive={activeBacktest?.id === backtest.id}
                  footerActions={footerActions}
                />
              </Box>
            )
          }
          return <Skeleton variant="rounded" animation="wave" height="100%" />
        }}
      />

      {activeIndex !== -1 && !isActiveVisible && (
        <Box
          position={'absolute'}
          bottom={8}
          left={0}
          right={0}
          display={'flex'}
          justifyContent={'center'}
          zIndex={1200}
        >
          <Button
            sx={{
              opacity: 0.86,
            }}
            variant="contained"
            color="primary"
            onClick={() => {
              if (virtuosoRef.current && activeIndex !== -1) {
                virtuosoRef.current.scrollToIndex({
                  index: activeIndex,
                  align: 'center',
                  behavior: 'smooth',
                })
              }
            }}
          >
            Go to selected backtest
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ReportCardList
